import { c as _c } from "react/compiler-runtime";
import React, { FunctionComponent, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import { useSpecificParams } from 'record-it-components/hooks/useSpecificParams/useSpecificParams';
import { HeaderConfig, ManageEntitiesContentLayout } from 'record-it-components/pageContentLayout/ManageEntitiesContentLayout/ManageEntitiesContentLayout';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { EntityName } from 'record-it-time-common/EntityName';
import { assertNotNullOrUndefined } from 'record-it-utils/utils/assert/assertNotNullOrUndefined';
import { TimeTrackingActivity } from '../../../classes/entities/TimeTrackingActivity/timeTrackingActivityTypes';
import { iconConfigurations } from '../../../classes/icon/iconConfigurations';
import { useEntities } from '../../../hooks/entity/useEntities/useEntities';
import { useEntityByIdOrOriginalId } from '../../../hooks/entity/useEntityByIdOrOriginalId';
import { useEntityRepository } from '../../../hooks/entity/useEntityRepository';
import { useEmptyCreateEntityButtonConfig } from '../../../hooks/useEmptyCreateEntityButtonConfig';
import { TimeTrackingActivityList } from '../../timeTrackingActivity/TimeTrackingActivityList/TimeTrackingActivityList';
import { useConfigureTimeTrackingActivityAdminPageBreadcrumbs } from './useConfigureTimeTrackingActivityAdminPageBreadcrumbs';
export type TimeTrackingActivityAdminPageProps = PropsWithoutChildren<Record<never, never>>;
export const TimeTrackingActivityAdminPage: FunctionComponent<TimeTrackingActivityAdminPageProps> = React.memo(props => {
  const $ = _c(20);
  prepareProps({
    props,
    ignoreChildren: true
  });
  const {
    t
  } = useTranslation();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      requiredParamNames: ["timeTrackingActivityCompilationId"],
      optionalParamNames: ["parentActivityId"]
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    parentActivityId,
    timeTrackingActivityCompilationId
  } = useSpecificParams(t0);
  const compilation = useEntityByIdOrOriginalId(EntityName.TimeTrackingActivityCompilation, timeTrackingActivityCompilationId);
  assertNotNullOrUndefined(compilation, `no compilation found for "${timeTrackingActivityCompilationId}"`);
  useConfigureTimeTrackingActivityAdminPageBreadcrumbs(compilation, parentActivityId);
  const activities = useEntities(EntityName.TimeTrackingActivity, "getByCompilationIdAndParentActivityId", compilation.id, parentActivityId ?? null);
  const timeTrackingActivityRepository = useEntityRepository(EntityName.TimeTrackingActivity);
  let t1;
  if ($[1] !== activities || $[2] !== compilation.id || $[3] !== compilation.ownerUserGroupId || $[4] !== parentActivityId || $[5] !== timeTrackingActivityRepository) {
    t1 = () => {
      const maxOrder = Math.max(...activities.map(_temp), 0);
      timeTrackingActivityRepository.create({
        order: maxOrder + 1,
        ownerUserGroupId: compilation.ownerUserGroupId,
        parentActivityId: parentActivityId ?? null,
        compilationId: compilation.id
      });
    };
    $[1] = activities;
    $[2] = compilation.id;
    $[3] = compilation.ownerUserGroupId;
    $[4] = parentActivityId;
    $[5] = timeTrackingActivityRepository;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const handleCreateEntity = useEventCallback(t1);
  let t2;
  if ($[7] !== t) {
    t2 = t("components.page.TimeTrackingActivityAdminPage.createEntity");
    $[7] = t;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== handleCreateEntity || $[10] !== t2) {
    t3 = {
      onCreateEntity: handleCreateEntity,
      text: t2
    };
    $[9] = handleCreateEntity;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const createEntityButtonConfig = useEmptyCreateEntityButtonConfig(t3);
  let t4;
  if ($[12] !== t) {
    t4 = t("components.page.TimeTrackingActivityAdminPage.pageTitle");
    $[12] = t;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== t4) {
    t5 = {
      iconConfig: iconConfigurations.prefixed.page.TimeTrackingActivityAdminPage.titleIcon,
      title: t4
    };
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const headerConfig = t5;
  const renderEntities = _temp2;
  let t6;
  if ($[16] !== activities || $[17] !== createEntityButtonConfig || $[18] !== headerConfig) {
    t6 = <ManageEntitiesContentLayout createEntityButtonConfig={createEntityButtonConfig} headerConfig={headerConfig} entities={activities} renderEntities={renderEntities} usePagination={false} />;
    $[16] = activities;
    $[17] = createEntityButtonConfig;
    $[18] = headerConfig;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  return t6;
});
function _temp(a) {
  return a.order;
}
function _temp2(currentPageActivities) {
  return <TimeTrackingActivityList timeTrackingActivities={currentPageActivities} data-testid="TimeTrackingActivityAdminPage.ActivityList" />;
}